import React, { useEffect, useMemo, useState } from 'react';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { BwjGrid } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { ActionButton, getTheme, IStyle, ITheme, mergeStyleSets, PanelType } from '@fluentui/react';
import { IPresenterTokenClaims } from '../../../../entities-generated/models/presenter-account/presenter-token-claims';
import { PresenterAccountService } from '../../../../services/account';
import { RowCol } from '../../../../framework/components/bwj-layout/rowcol';

xlationContext('Presenter account details');

interface IPresenterAccountDetailsPanelProps {
  invokeShowPanel: {} | undefined;
  onSignOutThisDevice: () => void;
  onSignOutAllDevices: () => void;
}

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    padding: 20,
    paddingTop: 0,
  };

  const accountContainer: IStyle = {
    paddingBottom: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.neutralLight}`
  };

  const initialBadge: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: theme.palette.themePrimary,
  };

  const initials: IStyle = {
    width: '90%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    color: theme.palette.white,
    fontSize: 32,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    letterSpacing: '1px',
  };

  const name: IStyle = {
    margin: 0,
    marginTop: 15,
    fontSize: 24,
    fontWeight: 600,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  };

  const email: IStyle = {
    marginTop: 10,
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: '1.5em',
    fontWeight: 'normal',
    color: theme.palette.neutralSecondary,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  };

  return mergeStyleSets({
    main,
    accountContainer,
    initialBadge,
    initials,
    name,
    email,
  });
};

// const subscriberId = 'Component.PresenterAccountDetailsPanel';

export const PresenterAccountDetailsPanel: React.FunctionComponent<IPresenterAccountDetailsPanelProps> = (props) => {
  const { invokeShowPanel } = props;
  const [showPanel, setShowPanel] = useState(false);
  const [account, setAccount] = useState<IPresenterTokenClaims | undefined>(undefined);
  const theme = getTheme();
  const styles = getStyleClasses(theme);
  const initials = useMemo(() => {
    if(!account) { return ''; }

    // only regard spaces and dashes as letter separators
    const source = account.fullName.replaceAll(/[^a-z0-9\s-]+/ig, '');
    const initials = source.split(/[\s-]+/g)
    .map(name => name[0]);

    return initials.join('');
  }, [account]);

  // on show panel invoked
  useEffect(() => {
    if(!!invokeShowPanel) {
      PresenterAccountService.myAccount()
      .then(acct => {
        setAccount(acct);
        setShowPanel(true);
      });
    }
  }, [invokeShowPanel]);

  const onSignOutThisDevice = () => {
    setShowPanel(false);
    props.onSignOutThisDevice();
  };

  const onSignOutAllDevices = () => {
    setShowPanel(false);
    props.onSignOutAllDevices();
  };

  return (
    <BwjSidePanel
      showPanel={showPanel}
      panelType={PanelType.smallFixedFar}
      footerButtons={[{
        buttonStyle: BwjButtonType.DefaultStandard,
        text: xlate('Close'),
        onClick: () => setShowPanel(false),
      }]}
      onDismiss={() => setShowPanel(false)}
      closeButtonAriaLabel={xlate('Close signed in account panel')}
    >
      <div className={styles.main}>
        <BwjGrid>
          <RowCol bottomSpacing={Spacing.Medium}>
            <section className={styles.accountContainer}>
              <div className={styles.initialBadge}>
                <h1 className={styles.initials}>{initials}</h1>
              </div>
              <h2 className={styles.name}>{account?.fullName ?? ''}</h2>
              <p className={styles.email}>{account?.email ?? ''}</p>
            </section>
          </RowCol>
          <RowCol bottomSpacing={Spacing.Medium}>
            <ActionButton
              iconProps={{ iconName: 'SignOut'}}
              onClick={onSignOutThisDevice}
            >
              {xlate('Sign out on this device only')}
            </ActionButton>
          </RowCol>
          <RowCol bottomSpacing={Spacing.Medium}>
            <ActionButton
              iconProps={{ iconName: 'Devices3'}}
              onClick={onSignOutAllDevices}
            >
              {xlate('Sign out on all devices')}
            </ActionButton>
          </RowCol>
        </BwjGrid>
      </div>
    </BwjSidePanel>
  );
};